import React, { useState, useEffect } from "react";
import { X, Loader2, Send } from "lucide-react";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";

const WhatsAppCloudTemplateModal = ({ open, onClose, ticket }) => {
  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [variables, setVariables] = useState({});
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open || !ticket?.whatsappId) return;

    setLoading(true);
    setSelectedTemplate(null);
    setVariables({});

    api.get(`/api/whatsapp-cloud/templates/${ticket.whatsappId}`)
      .then(({ data }) => {
        setTemplates(data.templates || []);
      })
      .catch((err) => {
        toastError(err);
        onClose();
      })
      .finally(() => {
        setLoading(false);
      });
  }, [open, ticket, onClose]);

  if (!open) return null;

  const handleSelectTemplate = (e) => {
    const name = e.target.value;
    const template = templates.find((t) => t.name === name);
    setSelectedTemplate(template);
    setVariables({});
  };

  // Find occurrences of {{1}}, {{2}}, etc. inside BODY component
  const getBodyComponentText = (template) => {
    const bodyComp = template?.components?.find((c) => c.type === "BODY");
    return bodyComp ? bodyComp.text : "";
  };

  const getVariableKeys = (template) => {
    const text = getBodyComponentText(template);
    const regex = /\{\{(\d+)\}\}/g;
    const matches = [];
    let match;
    while ((match = regex.exec(text)) !== null) {
      const num = parseInt(match[1], 10);
      if (!matches.includes(num)) {
        matches.push(num);
      }
    }
    return matches.sort((a, b) => a - b);
  };

  const handleVariableChange = (key, val) => {
    setVariables((prev) => ({ ...prev, [key]: val }));
  };

  const handleSend = async () => {
    if (!selectedTemplate) return;

    const keys = getVariableKeys(selectedTemplate);
    const variableValues = [];

    // Ensure all variables are filled
    for (const key of keys) {
      const val = variables[key];
      if (!val || val.trim() === "") {
        toast.error(`Por favor rellena la variable {{${key}}}`);
        return;
      }
      variableValues.push(val.trim());
    }

    // Substitute {{n}} placeholders so the conversation history shows the
    // actual text the customer received, not the raw template reference.
    const renderedBody = getBodyComponentText(selectedTemplate).replace(
      /\{\{(\d+)\}\}/g,
      (match, num) => variables[parseInt(num, 10)]?.trim() || match
    );

    setSending(true);
    try {
      await api.post("/api/whatsapp-cloud/templates/send", {
        ticketId: ticket.id,
        templateName: selectedTemplate.name,
        languageCode: selectedTemplate.language,
        variables: variableValues,
        renderedBody,
        // Categoría de la plantilla: es lo que determina cuánto cobra Meta.
        // Se manda desde acá porque la plantilla ya está cargada, y es lo que
        // permite atribuir el gasto a esta plantilla en Costos y márgenes.
        category: selectedTemplate.category
      });
      toast.success("Plantilla enviada correctamente");
      onClose();
    } catch (err) {
      toastError(err);
    } finally {
      setSending(false);
    }
  };

  const variableKeys = selectedTemplate ? getVariableKeys(selectedTemplate) : [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white dark:bg-slate-900 rounded-xl shadow-xl border border-slate-200 dark:border-slate-700 w-full max-w-md mx-4 overflow-hidden flex flex-col max-h-[85vh]">
        
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 dark:border-slate-700">
          <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
            Enviar Plantilla WhatsApp Cloud
          </h3>
          <button onClick={onClose} className="p-1 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 overflow-y-auto space-y-4 bg-slate-50 dark:bg-slate-800/10">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-8 gap-2">
              <Loader2 className="w-6 h-6 text-indigo-600 animate-spin" />
              <p className="text-xs text-slate-500">Cargando plantillas de Meta...</p>
            </div>
          ) : templates.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <p className="text-sm">No se encontraron plantillas aprobadas.</p>
              <p className="text-xs text-slate-400 mt-1">Asegúrate de tener plantillas en estado APPROVED en Meta.</p>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1.5">
                  Selecciona la plantilla
                </label>
                <select
                  onChange={handleSelectTemplate}
                  defaultValue=""
                  className="w-full px-3 py-2.5 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-all"
                >
                  <option value="" disabled>-- Selecciona plantilla --</option>
                  {templates.map((t) => (
                    <option key={`${t.name}_${t.language}`} value={t.name}>
                      {t.name} ({t.language})
                    </option>
                  ))}
                </select>
              </div>

              {selectedTemplate && (
                <div className="space-y-3 pt-2">
                  <div className="bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-3">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Vista Previa:</p>
                    <p className="text-xs text-slate-700 dark:text-slate-300 leading-relaxed font-mono whitespace-pre-wrap">
                      {getBodyComponentText(selectedTemplate)}
                    </p>
                  </div>

                  {variableKeys.length > 0 && (
                    <div className="space-y-3">
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                        Rellena las variables:
                      </p>
                      {variableKeys.map((key) => (
                        <div key={key} className="flex items-center gap-2">
                          <span className="w-12 text-xs font-bold text-indigo-600 dark:text-indigo-400 text-right">
                            {"{{"}{key}{"}}"}
                          </span>
                          <input
                            type="text"
                            value={variables[key] || ""}
                            onChange={(e) => handleVariableChange(key, e.target.value)}
                            placeholder={`Valor para variable ${key}`}
                            className="flex-1 px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-all"
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-slate-200 dark:border-slate-700">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
          >
            Cancelar
          </button>
          <button
            onClick={handleSend}
            disabled={!selectedTemplate || sending}
            className="flex items-center gap-1.5 px-4 py-2 text-sm font-semibold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Enviar
          </button>
        </div>
      </div>
    </div>
  );
};

export default WhatsAppCloudTemplateModal;
